import { format } from 'date-fns';

const DateHelpers = {
  midnight: 12,
  getUTCDate: () => {
    const date = new Date();
    return new Date(
      date.getUTCFullYear(),
      date.getUTCMonth(),
      date.getUTCDate(),
      date.getUTCHours(),
      date.getUTCMinutes(),
      date.getUTCSeconds(),
    );
  },

  toUTCDate: (date: Date) => {
    return new Date(
      date.getUTCFullYear(),
      date.getUTCMonth(),
      date.getUTCDate(),
      date.getUTCHours(),
      date.getUTCMinutes(),
      date.getUTCSeconds(),
      date.getUTCMilliseconds(),
    );
  },

  convertLocalDateToUTCDate(localDate: Date): Date {
    return new Date(
      Date.UTC(
        localDate.getFullYear(),
        localDate.getMonth(),
        localDate.getDate(),
        DateHelpers.midnight,
        0,
        0,
        0,
      ),
    );
  },

  /** Start of the given date's local calendar day (time zeroed). */
  startOfDay(date: Date): Date {
    const d = new Date(date);
    d.setHours(0, 0, 0, 0);
    return d;
  },

  /**
   * True when `effectiveDate`'s calendar day is on or before `onDate`'s calendar day.
   * Compares by local day so an equipment effectiveDate stored as noon UTC and a local
   * inspection date on the same calendar day are treated as effective (not future-dated).
   */
  isEffectiveOnOrBeforeDay(effectiveDate: Date, onDate: Date): boolean {
    return (
      DateHelpers.startOfDay(new Date(effectiveDate)).getTime() <=
      DateHelpers.startOfDay(new Date(onDate)).getTime()
    );
  },

  toStringX(date: string): string {
    return format(date, 'dd MMM yy · h:mma');
  },

  toUTCString(date: Date): string {
    const dateString = date.toISOString();

    return dateString.replace('T', ' ');
  },

  toLocalDateInputString(date: Date): string {
    const year = date.getFullYear();
    const month = String(date.getMonth() + 1).padStart(2, '0'); // Months are 0-based
    const day = String(date.getDate()).padStart(2, '0');

    return `${year}-${month}-${day}`;
  },

  convertToUTCDatePreservingLocalTime(d: Date): Date {
    const offsetMs = d.getTimezoneOffset() * 60 * 1000; // offset in milliseconds
    return new Date(d.getTime() - offsetMs);
  },
  getAmOrPm(date: Date): 'AM' | 'PM' {
    const hours = date.getHours();
    return hours < 12 ? 'AM' : 'PM';
  },
  convert24To12(time24: string): string {
    const [hourStr, minute, second] = time24.split(':');
    let hour = parseInt(hourStr, 10);
    const suffix = hour >= 12 ? 'PM' : 'AM';
    hour = hour % 12 || 12; // Convert 0 -> 12 for 12AM
    return `${hour}:${minute}:${second} ${suffix}`;
  },
  displayTimestamp({
    timestamp,
    convertToCommonTime,
  }: {
    timestamp: Date;
    convertToCommonTime?: boolean;
  }): string {
    const baseUtcMs = timestamp.getTime(); // always in UTC
    const adjustedUtc = convertToCommonTime
      ? new Date(
          baseUtcMs + DateHelpers.sydneyOffsetHours(timestamp) * 60 * 60 * 1000,
        )
      : timestamp;

    if (convertToCommonTime === true) {
      return DateHelpers.getUTCString(adjustedUtc);
    }

    const day = adjustedUtc.toDateString(); // e.g., "Mon Aug 11 2025"

    const time = adjustedUtc.toLocaleTimeString(); // e.g., "13:18:47"

    return `${day} · ${time}`;
  },

  /** Card-friendly timestamp: `26 Aug 2026 · 07:34` (no weekday, no seconds).
   *  Same DST-aware Sydney common-time shift as displayTimestamp. */
  displayShortTimestamp({
    timestamp,
    convertToCommonTime,
    includeTime = true,
  }: {
    timestamp: Date;
    convertToCommonTime?: boolean;
    includeTime?: boolean;
  }): string {
    const adjusted = convertToCommonTime
      ? new Date(
          timestamp.getTime() +
            DateHelpers.sydneyOffsetHours(timestamp) * 60 * 60 * 1000,
        )
      : timestamp;
    const utc = convertToCommonTime === true;
    const day = utc ? adjusted.getUTCDate() : adjusted.getDate();
    const month = adjusted.toLocaleString('en-US', {
      month: 'short',
      ...(utc ? { timeZone: 'UTC' } : {}),
    });
    const year = utc ? adjusted.getUTCFullYear() : adjusted.getFullYear();
    if (!includeTime) return `${day} ${month} ${year}`;
    const hours = (utc ? adjusted.getUTCHours() : adjusted.getHours())
      .toString()
      .padStart(2, '0');
    const minutes = (utc ? adjusted.getUTCMinutes() : adjusted.getMinutes())
      .toString()
      .padStart(2, '0');
    return `${day} ${month} ${year} · ${hours}:${minutes}`;
  },

  /** Sydney's UTC offset at a given instant: 10 in AEST, 11 during AEDT
   *  (Oct-Apr). The old fixed +10 made every synced timestamp - and the
   *  inspection-date read/write shift - an hour wrong half the year. */
  sydneyOffsetHours(atUtc: Date): number {
    const wallClockMs = (timeZone: string) =>
      new Date(atUtc.toLocaleString('en-US', { timeZone })).getTime();
    return Math.round(
      (wallClockMs('Australia/Sydney') - wallClockMs('UTC')) / (60 * 60 * 1000),
    );
  },

  /** Parse a native date-input value ('yyyy-mm-dd'). Returns null for cleared
   *  or partial input - building a Date from it produces an Invalid Date whose
   *  ISO conversion throws (DateField used to crash with an uncaught RangeError). */
  dateFromDateInput(value: string, useUTC: boolean): Date | null {
    if (!value) return null;
    const [year, month, day] = value.split('-').map(Number);
    if (!year || !month || !day) return null;
    const parsed = new Date();
    if (useUTC) {
      parsed.setUTCHours(DateHelpers.midnight, 0, 0, 0);
      parsed.setUTCFullYear(year, month - 1, day);
    } else {
      parsed.setHours(0, 0, 0, 0);
      parsed.setFullYear(year, month - 1, day);
    }
    return isNaN(parsed.getTime()) ? null : parsed;
  },

  getUTCString(adjustedUtc: Date): string {
    const year = adjustedUtc.getUTCFullYear();
    const month = adjustedUtc.toLocaleString('en-US', {
      month: 'short',
      timeZone: 'UTC',
    });
    const day = adjustedUtc.getUTCDate().toString().padStart(2, '0');
    const weekday = adjustedUtc.toLocaleString('en-US', {
      weekday: 'short',
      timeZone: 'UTC',
    });

    const hours = adjustedUtc.getUTCHours().toString().padStart(2, '0');
    const minutes = adjustedUtc.getUTCMinutes().toString().padStart(2, '0');
    const seconds = adjustedUtc.getUTCSeconds().toString().padStart(2, '0');

    return `${weekday} ${month} ${day} ${year} · ${hours}:${minutes}:${seconds}`;
  },

  /**
   * ISO-serialize a date-ish value for an observation. Date.prototype.toString()
   * produced locale junk ("Wed Aug 26 2026 ... GMT+1000 (...)") - the SQL audit
   * found EVERY synced Bucket.Installation.InstallDate in that format. An
   * unparseable input is returned as-is rather than becoming "Invalid Date".
   */
  toIsoDateString(value: Date | string): string {
    const date = value instanceof Date ? value : new Date(value);
    return isNaN(date.getTime()) ? String(value) : date.toISOString();
  },
};

export default DateHelpers;
